/**
 * Alshallal Pastry Company HR System - Authentication Context
 * Manages user authentication state and token handling
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('taqueen_token'));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Configure axios defaults
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // M13: the server can now end a session while the app is open - logout on
  // another device, a password change, an admin lock or the inactivity timeout.
  // Without this the person would sit on a screen quietly failing every request
  // until the 30-second session poll noticed. Only these two codes sign the user
  // out; an ordinary 401 or a permission 403 is left alone.
  //
  // NDA: the undertaking gate answers 403 NDA_REQUIRED on every route until it
  // is signed. That is not a sign-out - the person stays authenticated and is
  // sent to the consent screen. Handled here so it works no matter which
  // component made the call, including a URL typed straight into the address bar.
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const code = error.response?.data?.detail?.code;
        if (error.response?.status === 403 && code === 'NDA_REQUIRED') {
          setUser((current) => (current ? { ...current, nda_required: true } : current));
          if (window.location.pathname !== '/nda') {
            window.location.replace('/nda');
          }
          return Promise.reject(error);
        }
        if (error.response?.status === 403 && code === 'LOGIN_MODE_REQUIRED') {
          // The hat question is unanswered on this session - a second tab, or a
          // session that outlived a sign-out elsewhere. Put the chooser up
          // wherever they are rather than bouncing them to a URL.
          setUser((current) =>
            current ? { ...current, login_mode_required: true } : current);
          return Promise.reject(error);
        }
        if (error.response?.status === 401 &&
            ['SESSION_REVOKED', 'SESSION_IDLE_TIMEOUT',
             'ACCOUNT_LOCKED', 'ACCOUNT_INACTIVE'].includes(code)) {
          localStorage.removeItem('taqueen_token');
          localStorage.setItem(
            'taqueen_signed_out_reason',
            code === 'SESSION_IDLE_TIMEOUT' ? 'idle'
              : code === 'ACCOUNT_LOCKED' ? 'locked'
              : code === 'ACCOUNT_INACTIVE' ? 'inactive'
              : 'revoked'
          );
          setToken(null);
          setUser(null);
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  // Fetch current user on mount/token change
  const fetchUser = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/auth/me`);
      setUser(response.data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch user:', err);
      // Token invalid, clear it
      localStorage.removeItem('taqueen_token');
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  // Login function
  //
  // Two possible answers from the server. An ordinary account gets a token, as
  // it always did. An enrolled administrator gets `otp_required` and no token -
  // the caller then shows the code step. The device token, if this browser has
  // been trusted before, is sent so that step can be skipped.
  const login = async (identifier, password) => {
    try {
      setError(null);
      const deviceToken = localStorage.getItem('taqueen_device_token');
      const response = await axios.post(`${API_URL}/auth/login`, {
        identifier,
        password,
        ...(deviceToken ? { device_token: deviceToken } : {})
      });

      // Mandated by an administrator but not set up yet. No token is issued
      // until setup completes - without this branch the caller would store
      // `undefined` as the token and the person could not get in at all.
      if (response.data?.otp_enrollment_required) {
        return {
          success: false,
          otpEnrollmentRequired: true,
          setupTicket: response.data.setup_ticket,
          suggestedChannel: response.data.suggested_channel,
          suggestedMaskedTarget: response.data.suggested_masked_target,
          channelLocked: response.data.channel_locked,
          channelLockedBy: response.data.channel_locked_by,
          smsAvailable: response.data.sms_available,
          graceAllowed: response.data.grace_allowed,
          expiresIn: response.data.expires_in
        };
      }

      if (response.data?.otp_required) {
        return {
          success: false,
          otpRequired: true,
          challengeId: response.data.challenge_id,
          channel: response.data.channel,
          maskedTarget: response.data.masked_target,
          expiresIn: response.data.expires_in,
          resendAfter: response.data.resend_after,
          delivery: response.data.delivery,
          deliveryError: response.data.delivery_error,
          backupCodesAvailable: response.data.backup_codes_available,
          trustedDeviceDays: response.data.trusted_device_days
        };
      }

      const { access_token, user: userData } = response.data;
      
      localStorage.setItem('taqueen_token', access_token);
      setToken(access_token);
      setUser(userData);

      return { success: true, user: userData };
    } catch (err) {
      const errorMsg = err.response?.data?.detail?.error || 'Login failed';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    }
  };

  // Second step of sign-in: the one-time code, or a backup code.
  const verifyOtp = async (challengeId, code, rememberDevice) => {
    try {
      setError(null);
      const response = await axios.post(`${API_URL}/auth/otp/verify`, {
        challenge_id: challengeId,
        code,
        remember_device: !!rememberDevice
      });

      const { access_token, user: userData, trusted_device_token } = response.data;
      if (trusted_device_token) {
        localStorage.setItem('taqueen_device_token', trusted_device_token);
      }
      localStorage.setItem('taqueen_token', access_token);
      setToken(access_token);
      setUser(userData);

      return {
        success: true,
        user: userData,
        backupCodeUsed: response.data.backup_code_used,
        backupCodesRemaining: response.data.backup_codes_remaining
      };
    } catch (err) {
      const detail = err.response?.data?.detail;
      return {
        success: false,
        error: detail?.error || 'Could not verify that code',
        code: detail?.code,
        attemptsRemaining: detail?.attempts_remaining
      };
    }
  };

  // First-time setup, mid-sign-in. The password was already proved, so these
  // two calls carry the setup ticket instead of a token.
  const startOtpSetup = async (setupTicket, mobile, channel) => {
    try {
      const response = await axios.post(`${API_URL}/auth/otp/setup/start`, {
        setup_ticket: setupTicket,
        ...(mobile ? { mobile } : {}),
        ...(channel ? { channel } : {})
      });

      // Delivery failed and this account has no second factor yet, so the
      // server signed us in instead of trapping us at a gate that cannot open.
      if (response.data?.otp_setup_grace) {
        const { access_token, user: userData } = response.data;
        localStorage.setItem('taqueen_token', access_token);
        setToken(access_token);
        setUser(userData);
        return {
          success: true,
          grace: true,
          user: userData,
          deliveryError: response.data.delivery_error
        };
      }

      return {
        success: true,
        channel: response.data.channel,
        maskedTarget: response.data.masked_target
      };
    } catch (err) {
      const detail = err.response?.data?.detail;
      return { success: false, error: detail?.error || 'Could not send the code', code: detail?.code };
    }
  };

  const confirmOtpSetup = async (setupTicket, code, rememberDevice) => {
    try {
      const response = await axios.post(`${API_URL}/auth/otp/setup/confirm`, {
        setup_ticket: setupTicket,
        code,
        remember_device: !!rememberDevice
      });

      const { access_token, user: userData, trusted_device_token } = response.data;
      if (trusted_device_token) {
        localStorage.setItem('taqueen_device_token', trusted_device_token);
      }
      localStorage.setItem('taqueen_token', access_token);
      setToken(access_token);
      setUser(userData);

      return {
        success: true,
        user: userData,
        // Shown once and never again - only hashes are stored.
        backupCodes: response.data.backup_codes
      };
    } catch (err) {
      const detail = err.response?.data?.detail;
      return {
        success: false,
        error: detail?.error || 'Could not verify that code',
        code: detail?.code
      };
    }
  };

  const skipOtpSetup = async (setupTicket) => {
    try {
      const response = await axios.post(`${API_URL}/auth/otp/setup/skip`, {
        setup_ticket: setupTicket
      });
      const { access_token, user: userData } = response.data;
      localStorage.setItem('taqueen_token', access_token);
      setToken(access_token);
      setUser(userData);
      return { success: true, user: userData };
    } catch (err) {
      const detail = err.response?.data?.detail;
      return { success: false, error: detail?.error || 'Could not sign you in' };
    }
  };

  const resendOtp = async (challengeId) => {
    try {
      const response = await axios.post(`${API_URL}/auth/otp/resend`, {
        challenge_id: challengeId
      });
      return {
        success: true,
        resendAfter: response.data.resend_after,
        deliveryError: response.data.delivery_error
      };
    } catch (err) {
      const detail = err.response?.data?.detail;
      return { success: false, error: detail?.error || 'Could not send a new code' };
    }
  };

  // Logout function
  const logout = async () => {
    try {
      await axios.post(`${API_URL}/auth/logout`);
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      localStorage.removeItem('taqueen_token');
      setToken(null);
      setUser(null);
    }
  };

  // Change password function
  const changePassword = async (currentPassword, newPassword) => {
    try {
      setError(null);
      await axios.post(`${API_URL}/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      });

      // Refresh user data
      await fetchUser();
      return { success: true };
    } catch (err) {
      const errorMsg = err.response?.data?.detail?.error || 'Password change failed';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    }
  };

  // Request password reset
  const requestPasswordReset = async (identifier) => {
    try {
      setError(null);
      await axios.post(`${API_URL}/auth/password-reset/request`, { identifier });
      return { success: true };
    } catch (err) {
      const errorMsg = err.response?.data?.detail?.error || 'Request failed';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    }
  };

  // Login mode: "enter as Employee, or as <your role>?". The server is the
  // source of truth (it lives on the session row), so this is only ever read
  // from a user payload - never from localStorage. The remembered preference
  // below is a convenience for the two roles allowed to have one; the server
  // still refuses a remembered "role" for HR Manager, HR Admin, IT INF and
  // Super Admin, who must type their password every time.
  const rememberKey = (userId) => `taqueen_login_mode:${userId}`;

  const selectMode = async (mode, { password, remember } = {}) => {
    try {
      const response = await axios.post(`${API_URL}/auth/mode`, {
        mode,
        ...(password ? { password } : {}),
        remember: !!remember
      });
      setUser(response.data);
      if (response.data?.mode_can_be_remembered) {
        if (remember) {
          localStorage.setItem(rememberKey(response.data.id), mode);
        } else {
          localStorage.removeItem(rememberKey(response.data.id));
        }
      }
      return { success: true, user: response.data };
    } catch (err) {
      const detail = err.response?.data?.detail;
      return {
        success: false,
        error: detail?.error || 'Could not change mode',
        code: detail?.code
      };
    }
  };

  const rememberedMode = (userId) =>
    userId ? localStorage.getItem(rememberKey(userId)) : null;

  // Check if user has permission
  const hasPermission = (permission) => {
    if (!user) return false;
    // Super admin has all permissions
    if (user.role === 'super_admin') return true;
    // For now, basic role-based check (full permission mapping in backend)
    return true;
  };

  // Check if user has role
  // Super Admin is god of the system - bypasses all role checks
  const hasRole = (...roles) => {
    if (!user) return false;
    // Super Admin has ALL roles' capabilities - god mode
    if (user.role === 'super_admin') return true;
    return roles.includes(user.role);
  };

  const value = {
    user,
    token,
    loading,
    error,
    isAuthenticated: !!user,
    login,
    verifyOtp,
    resendOtp,
    startOtpSetup,
    confirmOtpSetup,
    skipOtpSetup,
    logout,
    changePassword,
    requestPasswordReset,
    hasPermission,
    hasRole,
    // Login mode
    mode: user?.login_mode || null,
    modeRequired: !!user?.login_mode_required,
    actualRole: user?.actual_role || user?.role || null,
    employeeModeAvailable: !!user?.employee_mode_available,
    modeStepUpRequired: !!user?.mode_step_up_required,
    modeCanBeRemembered: !!user?.mode_can_be_remembered,
    selectMode,
    rememberedMode,
    refreshUser: fetchUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
